import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Scale, the horizontal 1 to 5 number scale.
 *
 * CSS selector: `mobile/organisms.css` `.ds-scale` (+ `__tile`,
 * `__tile--selected`, `__hint`). The CSS lives in the organisms layer, which
 * another workstream owns; this file only reads it. The component sits with the
 * molecules alongside `<OptionCard>`, the other component the registry flags as
 * missing.
 *
 * Doctrine: choosing a point on the scale is a commit, so the selected tile
 * inverts to ink rather than taking an accent fill, exactly like `<OptionCard>`
 * and `<Chip>`. Tiles are square and at least 44px wide, so the tap target
 * comes from the tile geometry and never from a larger number.
 *
 * The hint row carries the two anchor labels, low on the left and high on the
 * right, which is what makes an unlabelled number row legible at all.
 *
 * Rendered as a `radiogroup`, because a scale is a single choice among an
 * ordered set. Pass `label` so the group announces what is being rated.
 *
 * The hint row is a sibling of `.ds-scale`, not a child, because `.ds-scale` is
 * a flex row of tiles and a nested hint would become a sixth tile. That is why
 * this component has an outer wrapper, and why `className` and the rest props
 * land on that wrapper rather than on the tile row.
 */
export const Scale = React.forwardRef(function Scale(
  {
    className,
    min = 1,
    max = 5,
    value,
    onSelect,
    lowHint,
    highHint,
    label,
    tileLabel = (point) => `Rate ${point}`,
    ...rest
  },
  ref,
) {
  const points = [];
  for (let point = min; point <= max; point += 1) points.push(point);

  return (
    <div ref={ref} role="radiogroup" aria-label={label} className={cn(className)} {...rest}>
      <div className="ds-scale">
        {points.map((point) => (
          <button
            key={point}
            type="button"
            role="radio"
            aria-checked={value === point}
            aria-label={tileLabel(point)}
            onClick={() => {
              if (onSelect) onSelect(point);
            }}
            className={cn('ds-scale__tile', value === point && 'ds-scale__tile--selected')}
          >
            {point}
          </button>
        ))}
      </div>
      {lowHint || highHint ? (
        <div className="ds-scale__hint">
          <span>{lowHint}</span>
          <span>{highHint}</span>
        </div>
      ) : null}
    </div>
  );
});

export default Scale;
